import type {
  AnalyticsDeliveryMode,
  AnalyticsIdentity,
  AnalyticsParams,
  AnalyticsStatus,
  PageTrackingData,
} from './types';
import { NATIVE_PLATFORM } from '@/lib/nativeBridge';
import { dispatchIosTrackingEvent } from './iosTracking';

type AnalyticsCall = {
  eventName: string;
  payload: Record<string, string>;
  ga4UserId?: string | null;
};

type TealiumCall = AnalyticsCall & {
  type: 'view' | 'link';
};

export type TealiumDispatchStatus = 'dispatched' | 'queued' | 'failed';

const MAX_QUEUED_CALLS = 100;
const ANALYTICS_DEBUG_STORAGE_KEY = 'dt:analytics-debug';

let deliveryMode: AnalyticsDeliveryMode = 'tealium';

let identity: AnalyticsIdentity = { user_type: 'logged out' };
let hasAuthenticatedIdentity = false;
let hasDelegatedInitialTeamEntryView = false;
let lastAppliedGa4UserId: string | null | undefined;
const queuedTealiumCalls: TealiumCall[] = [];
const queuedGa4Calls: AnalyticsCall[] = [];

const isGa4DeliveryEnabled = () => deliveryMode === 'ga4' || deliveryMode === 'dual';

const isDebugEnabled = () => {
  if (import.meta.env.DEV || window.__DT_ANALYTICS_DEBUG__) return true;

  try {
    return window.localStorage.getItem(ANALYTICS_DEBUG_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const debug = (message: string, details?: unknown) => {
  if (!isDebugEnabled()) return;
  console.info(`[analytics] ${message}`, details ?? '');
};

const sanitize = (params: AnalyticsParams): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined)
  );

const withIdentity = (params: AnalyticsParams) =>
  sanitize({
    ...params,
    customer_id: identity.customer_id,
    user_type: identity.user_type,
  });

const getGa4UserId = (): string | null | undefined => {
  if (identity.user_type === 'logged in' && identity.customer_id) return identity.customer_id;
  return hasAuthenticatedIdentity ? null : undefined;
};

const applyGa4UserId = (userId: string | null | undefined) => {
  if (!window.gtag || userId === undefined || userId === lastAppliedGa4UserId) return;

  try {
    window.gtag('set', { user_id: userId });
    lastAppliedGa4UserId = userId;
    debug(userId === null ? 'cleared GA4 user_id' : 'set GA4 user_id');
  } catch (error) {
    debug('failed to update GA4 user_id', error);
  }
};

const enqueue = <T>(queue: T[], call: T) => {
  if (queue.length >= MAX_QUEUED_CALLS) queue.shift();
  queue.push(call);
};

const dispatchTealium = (call: TealiumCall): TealiumDispatchStatus => {
  const method = window.utag?.[call.type];
  if (!method) {
    enqueue(queuedTealiumCalls, call);
    debug(`queued Tealium ${call.type}`, call);
    return 'queued';
  }

  try {
    method.call(window.utag, call.payload);
    debug(`dispatched Tealium ${call.type}`, call);
    return 'dispatched';
  } catch (error) {
    debug(`failed to dispatch Tealium ${call.type}`, error);
    return 'failed';
  }
};

const dispatchInteraction = (
  eventName: string,
  eventPayload: Record<string, string>,
  tealiumPayload: Record<string, string>
): TealiumDispatchStatus => {
  const payloadEventName = eventPayload.name ?? eventPayload.eventName;
  // ? `Team Created` events should be send via both native SDK and Tealium Web SDK
  // ? https://low6.slack.com/archives/C0AB3KXJLUV/p1787833590651989
  const isNativeTrackableEvents =
    payloadEventName && /team(\s|_)created/gim.test(payloadEventName.toLowerCase());

  if (NATIVE_PLATFORM === 'ios' && isNativeTrackableEvents) {
    const status = dispatchIosTrackingEvent(eventPayload);
    debug(`${status === 'dispatched' ? 'dispatched' : 'failed to dispatch'} iOS event`, {
      eventName,
      payload: eventPayload,
    });
    // ? native status is skippable, can rely on tealium result
    // return status;
  }

  return dispatchTealium({ type: 'link', eventName, payload: tealiumPayload });
};

const dispatchGa4 = (call: AnalyticsCall) => {
  if (!window.gtag) {
    enqueue(queuedGa4Calls, call);
    debug('queued GA4 event', call);
    return;
  }

  applyGa4UserId(call.ga4UserId);
  try {
    window.gtag('event', call.eventName, call.payload);
    debug('dispatched GA4 event', call);
  } catch (error) {
    debug('failed to dispatch GA4 event', error);
  }
};

export const flushAnalyticsQueues = () => {
  if (window.utag?.view && window.utag?.link) {
    const pending = queuedTealiumCalls.splice(0);
    pending.forEach(dispatchTealium);
  }

  if (window.gtag && isGa4DeliveryEnabled()) {
    const pending = queuedGa4Calls.splice(0);
    pending.forEach(dispatchGa4);
    applyGa4UserId(getGa4UserId());
  }
};

export const getAnalyticsStatus = (): AnalyticsStatus => ({
  deliveryMode,
  tealiumReady: Boolean(window.utag?.view && window.utag?.link),
  ga4Ready: Boolean(window.gtag),
  queuedTealiumCalls: queuedTealiumCalls.length,
  queuedGa4Calls: queuedGa4Calls.length,
});

export const startAnalyticsClient = () => {
  flushAnalyticsQueues();
  debug('provider initialized', getAnalyticsStatus());

  const intervalId = window.setInterval(flushAnalyticsQueues, 500);
  return () => window.clearInterval(intervalId);
};

export const setAnalyticsIdentity = (nextIdentity: AnalyticsIdentity) => {
  if (nextIdentity.user_type === 'logged in' && nextIdentity.customer_id) {
    hasAuthenticatedIdentity = true;
  }

  identity = nextIdentity;

  if (!identity.customer_id && window.utag_data) {
    delete window.utag_data.customer_id;
  }

  if (NATIVE_PLATFORM !== 'ios' && queuedGa4Calls.length === 0 && isGa4DeliveryEnabled()) {
    applyGa4UserId(getGa4UserId());
  }
};

export const setAnalyticsDeliveryMode = (mode: AnalyticsDeliveryMode) => {
  deliveryMode = mode;
};

export const trackAnalyticsPageView = (
  pathname: string,
  search: string,
  pageData: PageTrackingData
) => {
  const payload = withIdentity({
    ...pageData,
    page_path: `${pathname}${search}`,
    page_url: window.location.href,
  });

  // if (NATIVE_PLATFORM === 'ios') {
  //   const status = dispatchIosTrackingScreen(pageData.page_name, payload);
  //   debug(`${status === 'dispatched' ? 'dispatched' : 'failed to dispatch'} iOS screen`, {
  //     screenName: pageData.page_name,
  //     payload,
  //   });
  //   return;
  // }

  window.utag_data = {
    ...(window.utag_data ?? {}),
    ...payload,
  };

  if (deliveryMode === 'tealium' || deliveryMode === 'dual') {
    const shouldUseAutomaticInitialView =
      deliveryMode === 'tealium' &&
      pathname === '/' &&
      !hasDelegatedInitialTeamEntryView &&
      !window.utag?.view;

    if (shouldUseAutomaticInitialView) {
      hasDelegatedInitialTeamEntryView = true;
      debug('delegated initial Team Entry view to Tealium loader');
    } else {
      dispatchTealium({ type: 'view', eventName: 'page_view', payload });
    }
  }

  if (deliveryMode === 'ga4' || deliveryMode === 'dual') {
    dispatchGa4({
      eventName: 'page_view',
      ga4UserId: getGa4UserId(),
      payload: {
        ...payload,
        page_title: pageData.page_name,
        page_location: window.location.href,
      },
    });
  }
};

export const trackAnalyticsEvent = (eventName: string, params: AnalyticsParams = {}) => {
  const ga4Payload = withIdentity(params);

  if (deliveryMode === 'tealium' || deliveryMode === 'dual') {
    dispatchInteraction(eventName, ga4Payload, {
      event_navigation_action: 'navigation',
      event_navigation_name: eventName,
      event_navigation_browsing_method: ga4Payload.event_category ?? '',
      additional_details_1: ga4Payload.event_action ?? '',
      ga4_event_name: eventName,
      ...ga4Payload,
    });
  }

  if (NATIVE_PLATFORM !== 'ios' && (deliveryMode === 'ga4' || deliveryMode === 'dual')) {
    dispatchGa4({ eventName, payload: ga4Payload, ga4UserId: getGa4UserId() });
  }
};

/**
 * Sends one contract-backed interaction through the platform delivery boundary.
 * Web and Android keep Tealium; iOS uses its confirmed native bridge contract.
 */
export const trackTealiumEvent = (
  eventName: string,
  params: AnalyticsParams = {}
): TealiumDispatchStatus => {
  const payload = withIdentity(params);

  return dispatchInteraction(eventName, payload, {
    event_navigation_action: 'navigation',
    event_navigation_name: eventName,
    event_navigation_browsing_method: payload.event_category ?? '',
    additional_details_1: payload.event_action ?? '',
    ...payload,
  });
};

export const resetAnalyticsClientForTests = () => {
  identity = { user_type: 'logged out' };
  hasAuthenticatedIdentity = false;
  hasDelegatedInitialTeamEntryView = false;
  lastAppliedGa4UserId = undefined;
  deliveryMode = 'tealium';
  queuedTealiumCalls.splice(0);
  queuedGa4Calls.splice(0);
};
